import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  ArrowLeft, 
  MapPin, 
  Calendar,
  Clock,
  Navigation,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  Home,
  Music,
  Mic,
  Piano,
  Guitar,
  Drum,
  Wind,
  Music2,
  Music4,
  Disc,
  Sparkles
} from "lucide-react";
import { PublicFooter } from "./PublicFooter";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Analytics tracking - fire-and-forget, silent fail
const trackEvent = async (eventType, data = {}) => {
  try {
    await axios.post(`${API_URL}/api/analytics/track`, {
      event_type: eventType,
      ...data
    });
  } catch (e) { /* Silent fail */ }
};

// Track wizard_start only once per session
const trackWizardStartOnce = () => {
  if (sessionStorage.getItem('wizard_start_tracked')) return;
  sessionStorage.setItem('wizard_start_tracked', '1');
  trackEvent('wizard_start');
};

// Helper to get UI text with placeholder replacement
const getUiText = (settings, key, placeholders = {}, fallback = '') => {
  let text = settings?.ui_texts?.[key] || fallback;
  // Replace placeholders like {gemeinde} or {instrument}
  Object.entries(placeholders).forEach(([placeholder, value]) => {
    text = text.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), value || '');
  });
  return text;
};

// Lucide icon mapping for instruments
const LUCIDE_ICONS = {
  Music, Mic, Piano, Guitar, Drum, Wind, Music2, Music4, Disc
};

// Get icon component by name (returns Music as default)
const getInstrumentIcon = (iconName) => {
  if (!iconName) return Music;
  return LUCIDE_ICONS[iconName] || Music;
};

// Instrument Icons for groups (fallback emojis)
const INSTRUMENT_ICONS = {
  "Tasteninstrumente": "🎹",
  "Saiteninstrumente": "🎸",
  "Blechblasinstrumente": "🎺",
  "Holzblasinstrumente": "🎷",
  "Schlagwerk": "🥁",
  "Gesang": "🎤",
  "Sonstiges": "🎵",
};

// Scroll Indicator Component - shows an animated swipe-down icon when content is scrollable (mobile only)
function ScrollIndicator() {
  const [showIndicator, setShowIndicator] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    const handleScroll = () => {
      // Hide indicator when user scrolls down more than 100px
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowIndicator(scrollTop < 100);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Only show on mobile
  if (!showIndicator || !isMobile) return null;
  
  return (
    <motion.div 
      className="fixed bottom-24 left-0 right-0 z-30 pointer-events-none flex justify-center md:hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8, duration: 0.4 }}
    >
      {/* Gradient fade overlay */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#faf9f7] via-[#faf9f7]/80 to-transparent pointer-events-none" />
      
      {/* Scroll hint with SVG icon */}
      <motion.button
        className="relative flex items-center justify-center pointer-events-auto opacity-40 hover:opacity-60 transition-opacity"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        onClick={() => window.scrollBy({ top: 300, behavior: 'smooth' })}
        aria-label="Nach unten scrollen"
      >
        <img 
          src="/swipe-down.svg" 
          alt="Mehr anzeigen" 
          className="w-12 h-12"
          style={{ filter: 'opacity(0.7)' }}
        />
      </motion.button>
    </motion.div>
  );
}

export function WizardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check URL params immediately to determine initial step
  const initialGemeindeId = searchParams.get("gemeinde");
  const initialGemeindeName = searchParams.get("name");
  const hasDirectGemeinde = !!(initialGemeindeId && initialGemeindeName);
  
  const [currentStep, setCurrentStep] = useState(hasDirectGemeinde ? 3 : 1);
  const [loading, setLoading] = useState(true);
  
  // Data from API
  const [instrumente, setInstrumente] = useState([]);
  const [instrumentengruppen, setInstrumentengruppen] = useState([]);
  const [gemeinden, setGemeinden] = useState([]);
  const [angebote, setAngebote] = useState([]);
  const [lehrpersonen, setLehrpersonen] = useState([]);
  const [unterrichtsorte, setUnterrichtsorte] = useState([]);
  
  // User selections - support multiple instruments
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null); // Legacy for single
  const [selectedInstrumentName, setSelectedInstrumentName] = useState("");
  const [selectedGemeinde, setSelectedGemeinde] = useState(initialGemeindeId || null);
  const [selectedGemeindeName, setSelectedGemeindeName] = useState(initialGemeindeName || "");
  
  // Selected teacher filter (when coming from teacher overlay)
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedTeacherName, setSelectedTeacherName] = useState("");
  
  // Results
  const [results, setResults] = useState([]);
  const [allResultsInGemeinde, setAllResultsInGemeinde] = useState([]);
  const [alternativeGemeinden, setAlternativeGemeinden] = useState([]);
  
  // Filter for results page
  const [activeFilter, setActiveFilter] = useState("all");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Search for instruments
  const [instrumentSearch, setInstrumentSearch] = useState("");
  
  // Public settings (for dates)
  const [publicSettings, setPublicSettings] = useState(null);
  
  // Teacher filter view (instead of modal)
  const [teacherFilter, setTeacherFilter] = useState(null); // { teacher, gemeinden }

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [instrumenteRes, gruppenRes, gemeindenRes, angeboteRes, lehrpersonenRes, unterrichtsortRes, settingsRes] = await Promise.all([
          axios.get(`${API_URL}/api/instrumente`),
          axios.get(`${API_URL}/api/instrumentengruppen`),
          axios.get(`${API_URL}/api/gemeinden`),
          axios.get(`${API_URL}/api/angebote`),
          axios.get(`${API_URL}/api/lehrpersonen`),
          axios.get(`${API_URL}/api/unterrichtsorte`),
          axios.get(`${API_URL}/api/public/settings`).catch(() => ({ data: null })),
        ]);
        setInstrumente(instrumenteRes.data);
        setInstrumentengruppen(gruppenRes.data); // Already sorted by sort_order from backend
        setGemeinden(gemeindenRes.data);
        setAngebote(angeboteRes.data);
        setLehrpersonen(lehrpersonenRes.data);
        setUnterrichtsorte(unterrichtsortRes.data);
        setPublicSettings(settingsRes.data);
        setLoading(false);
        
        // Track wizard start (only once per session)
        trackWizardStartOnce();

        // Check for direct gemeinde from URL params
        const gemeindeId = searchParams.get("gemeinde");
        const gemeindeName = searchParams.get("name");
        if (gemeindeId && gemeindeName) {
          setSelectedInstruments([]);
          setSelectedInstrument("all");
          // Don't use setTimeout - process directly since step is already 3
          handleGemeindeSelectDirect(gemeindeId, gemeindeName, angeboteRes.data, gemeindenRes.data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, [searchParams]);

  // Create a lookup for group info (emoji, sort_order)
  const gruppenLookup = instrumentengruppen.reduce((acc, g) => {
    acc[g.name] = { icon: g.icon || '🎵', sortOrder: g.sort_order || 0, id: g.id };
    return acc;
  }, {});

  // Group instruments by category and sort by group order
  const instrumentsByGroup = instrumente.reduce((acc, inst) => {
    const group = inst.gruppe_name || inst.instrumentengruppe_name || "Sonstiges";
    if (!acc[group]) acc[group] = [];
    acc[group].push(inst);
    return acc;
  }, {});

  // Get sorted group names based on instrumentengruppen order
  const sortedGroupNames = Object.keys(instrumentsByGroup).sort((a, b) => {
    const orderA = gruppenLookup[a]?.sortOrder ?? 999;
    const orderB = gruppenLookup[b]?.sortOrder ?? 999;
    return orderA - orderB;
  });

  // Create lehrpersonen lookup
  const lehrpersonenById = lehrpersonen.reduce((acc, lp) => {
    acc[lp.id] = lp;
    return acc;
  }, {});

  // Helper: Get instrument names from IDs
  const getInstrumentNames = (ids) => {
    return ids.map(id => instrumente.find(i => i.id === id)?.name).filter(Boolean);
  };

  // Handle instrument toggle (multi-select)
  const handleInstrumentToggle = (instrumentId, instrumentName) => {
    if (selectedInstruments.includes(instrumentId)) {
      // Remove
      const newSelection = selectedInstruments.filter(id => id !== instrumentId);
      setSelectedInstruments(newSelection);
      // Update legacy single selection
      if (newSelection.length === 1) {
        const inst = instrumente.find(i => i.id === newSelection[0]);
        setSelectedInstrument(newSelection[0]);
        setSelectedInstrumentName(inst?.name || "");
      } else if (newSelection.length === 0) {
        setSelectedInstrument(null);
        setSelectedInstrumentName("");
      } else {
        setSelectedInstrument("multiple");
        const names = getInstrumentNames(newSelection);
        setSelectedInstrumentName(names.join(" und "));
      }
    } else {
      // Add
      const newSelection = [...selectedInstruments, instrumentId];
      setSelectedInstruments(newSelection);
      if (newSelection.length === 1) {
        setSelectedInstrument(instrumentId);
        setSelectedInstrumentName(instrumentName || "");
      } else {
        setSelectedInstrument("multiple");
        const names = getInstrumentNames(newSelection);
        setSelectedInstrumentName(names.join(" und "));
      }
    }
  };

  // Continue to step 2
  const handleContinueToStep2 = () => {
    if (selectedInstruments.length === 0) {
      // Show all
      setSelectedInstrument("all");
      setSelectedInstrumentName("");
    }
    setCurrentStep(2);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle "Ich weiß noch nicht" selection
  const handleDontKnow = () => {
    setSelectedInstruments([]);
    setSelectedInstrument("all");
    setSelectedInstrumentName("");
    setCurrentStep(2);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get gemeinden that have any of the selected instruments - ONLY for results filtering
  // Step 2 always shows ALL gemeinden, filtering happens only on results page
  const gemeindenWithSelectedInstrument = selectedInstruments.length === 0 || selectedInstrument === "all"
    ? gemeinden
    : gemeinden.filter((g) => 
        angebote.some((a) => 
          a.gemeinde_id === g.id && 
          selectedInstruments.some(instId => 
            a.instrument_ids?.includes(instId) || a.instrument_id === instId
          )
        )
      );

  // Calculate date for weekday based on event settings
  const getDateForWeekday = (weekday) => {
    if (!publicSettings?.show_weekday_dates) return null;
    
    // Parse event_dates string like "24. - 28. März 2025"
    const eventDates = publicSettings?.event_dates;
    if (!eventDates) return null;
    
    const match = eventDates.match(/(\d+)\.\s*-\s*(\d+)\.\s*([^\s]+)\s*(\d{4})/);
    if (!match) return null;
    
    const [, startDay, endDay, month, year] = match;
    
    // Map German month names to month numbers
    const monthMap = {
      'Januar': 0, 'Februar': 1, 'März': 2, 'April': 3, 'Mai': 4, 'Juni': 5,
      'Juli': 6, 'August': 7, 'September': 8, 'Oktober': 9, 'November': 10, 'Dezember': 11
    };
    
    const monthNum = monthMap[month];
    if (monthNum === undefined) return null;
    
    const startDate = new Date(parseInt(year), monthNum, parseInt(startDay));
    const endDate = new Date(parseInt(year), monthNum, parseInt(endDay));
    
    const weekdayMap = {
      'Montag': 1, 'Dienstag': 2, 'Mittwoch': 3, 'Donnerstag': 4, 
      'Freitag': 5, 'Samstag': 6, 'Sonntag': 0
    };
    
    const targetDay = weekdayMap[weekday];
    const startDayOfWeek = startDate.getDay();
    
    // Find the date of this weekday within the event week
    let diff = targetDay - startDayOfWeek;
    if (diff < 0) diff += 7;
    
    const targetDate = new Date(startDate);
    targetDate.setDate(startDate.getDate() + diff);
    
    // Check if within event range
    if (targetDate > endDate) return null;
    
    return targetDate.toLocaleDateString('de-AT', { day: 'numeric', month: 'long' });
  };

  // Direct gemeinde select (from URL params)
  const handleGemeindeSelectDirect = (gemeindeId, gemeindeName, allAngebote, allGemeinden) => {
    setSelectedGemeinde(gemeindeId);
    setSelectedGemeindeName(gemeindeName);
    setActiveFilter("all");
    
    const resultsInGemeinde = allAngebote.filter((a) => a.gemeinde_id === gemeindeId);
    
    if (resultsInGemeinde.length === 0) {
      const otherGemeinden = [...new Set(allAngebote.map((a) => a.gemeinde_id))]
        .filter((id) => id !== gemeindeId);
      const alternatives = otherGemeinden
        .map((id) => allGemeinden.find((g) => g.id === id))
        .filter(Boolean);
      setAlternativeGemeinden(alternatives);
      setResults([]);
      setAllResultsInGemeinde([]);
    } else {
      setAlternativeGemeinden([]);
      setResults(resultsInGemeinde);
      setAllResultsInGemeinde(resultsInGemeinde);
    }
    setCurrentStep(3);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle gemeinde selection
  const handleGemeindeSelect = (gemeindeId, gemeindeName, overrideInstruments = null) => {
    // Clear teacher filter when selecting gemeinde normally
    setSelectedTeacherId(null);
    setSelectedTeacherName("");
    
    setSelectedGemeinde(gemeindeId);
    setSelectedGemeindeName(gemeindeName);
    setActiveFilter("all");
    
    // Get all results in gemeinde (for filter chips)
    const allInGemeinde = angebote.filter((a) => a.gemeinde_id === gemeindeId);
    setAllResultsInGemeinde(allInGemeinde);
    
    let filteredAngebote = angebote;
    
    // Use override instruments if provided, otherwise use current selection
    const instrumentsToFilter = overrideInstruments !== null ? overrideInstruments : selectedInstruments;
    
    // Filter by selected instruments (support multiple)
    if (instrumentsToFilter.length > 0) {
      filteredAngebote = filteredAngebote.filter((a) =>
        instrumentsToFilter.some(instId => 
          a.instrument_ids?.includes(instId) || a.instrument_id === instId
        )
      );
    } else if (selectedInstrument && selectedInstrument !== "all" && selectedInstrument !== "multiple") {
      filteredAngebote = filteredAngebote.filter((a) =>
        a.instrument_ids?.includes(selectedInstrument) || a.instrument_id === selectedInstrument
      );
    }
    
    const resultsInGemeinde = filteredAngebote.filter((a) => a.gemeinde_id === gemeindeId);
    
    // Get instrument names for tracking
    const instrument_names = instrumentsToFilter
      .map(id => instrumente.find(i => i.id === id)?.name)
      .filter(Boolean);
    
    if (resultsInGemeinde.length === 0) {
      const otherGemeinden = [...new Set(filteredAngebote.map((a) => a.gemeinde_id))]
        .filter((id) => id !== gemeindeId);
      const alternatives = otherGemeinden
        .map((id) => gemeinden.find((g) => g.id === id))
        .filter(Boolean);
      setAlternativeGemeinden(alternatives);
      setResults([]);
      // Track no results
      trackEvent('no_results', {
        gemeinde_id: gemeindeId,
        gemeinde_name: gemeindeName,
        instrument_ids: instrumentsToFilter,
        instrument_names,
        results_count: 0
      });
    } else {
      setAlternativeGemeinden([]);
      setResults(resultsInGemeinde);
      // Track successful search
      trackEvent('search_complete', {
        gemeinde_id: gemeindeId,
        gemeinde_name: gemeindeName,
        instrument_ids: instrumentsToFilter,
        instrument_names,
        results_count: resultsInGemeinde.length
      });
    }
    setCurrentStep(3);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle gemeinde select with teacher filter (from teacher overlay)
  const handleGemeindeSelectWithTeacher = (gemeindeId, gemeindeName, teacherId, teacherName) => {
    setSelectedTeacherId(teacherId);
    setSelectedTeacherName(teacherName);
    setSelectedGemeinde(gemeindeId);
    setSelectedGemeindeName(gemeindeName);
    setActiveFilter("all");
    
    // Get all results in gemeinde (for filter chips)
    const allInGemeinde = angebote.filter((a) => a.gemeinde_id === gemeindeId);
    setAllResultsInGemeinde(allInGemeinde);
    
    // Filter by teacher AND gemeinde
    const resultsForTeacher = angebote.filter((a) => 
      a.gemeinde_id === gemeindeId && a.lehrperson_id === teacherId
    );
    
    setAlternativeGemeinden([]);
    setResults(resultsForTeacher);
    setCurrentStep(3);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Clear teacher filter and show all offers in gemeinde
  const clearTeacherFilter = () => {
    setSelectedTeacherId(null);
    setSelectedTeacherName("");
    // Re-filter to show all offers in gemeinde
    const allInGemeinde = angebote.filter((a) => a.gemeinde_id === selectedGemeinde);
    setResults(allInGemeinde);
    setAllResultsInGemeinde(allInGemeinde);
  };

  // Back navigation - keeps selections
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Scroll to top on mobile
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (currentStep === 3) {
        // Keep gemeinde selection when going back to step 2
        setResults([]);
        setAlternativeGemeinden([]);
        setActiveFilter("all");
      }
      // Don't reset instrument selection when going back - user can change it if needed
    } else {
      navigate("/");
    }
  };

  // Get available instruments in current gemeinde (for filter chips)
  // Directly computed from results - no need for separate state
  const availableInstrumentsInGemeinde = [...new Set(
    results.map((a) => a.instrument_name)
  )].filter(Boolean).sort();

  // Separate regular results from special offers (gemeindeuebergreifend)
  const regularResults = results.filter(r => !r.gemeindeuebergreifend);
  const specialOffers = angebote.filter(a => a.gemeindeuebergreifend);

  // Filter results based on active filter
  const filteredResults = activeFilter === "all" 
    ? regularResults 
    : regularResults.filter((r) => r.instrument_name === activeFilter);
  
  // Show special offers only when "Alle" is selected AND no teacher filter is active
  const filteredSpecialOffers = (activeFilter === "all" && !selectedTeacherId) ? specialOffers : [];

  // Handle filter change
  const handleFilterChange = (instrumentName) => {
    setActiveFilter(instrumentName);
  };

  // Group filtered results by instrument, then by weekday
  const resultsByInstrument = filteredResults.reduce((acc, result) => {
    const instrument = result.instrument_name || "Unbekannt";
    if (!acc[instrument]) acc[instrument] = {};
    const day = result.wochentag || "Unbekannt";
    if (!acc[instrument][day]) acc[instrument][day] = [];
    acc[instrument][day].push(result);
    return acc;
  }, {});

  const weekdayOrder = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-[#FFF1F2] to-white flex items-center justify-center">
        <div className="animate-pulse text-gray-500 text-lg">Laden...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#FFF1F2] to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            data-testid="wizard-back-btn"
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-[#cf2e2e] transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Zurück</span>
          </button>
          
          {/* Progress indicator */}
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((step) => (
              <div
                key={step}
                className={`w-3 h-3 rounded-full transition-all ${
                  step === currentStep
                    ? "bg-[#cf2e2e] w-8"
                    : step < currentStep
                    ? "bg-[#cf2e2e]"
                    : "bg-gray-200"
                }`}
              />
            ))}
          </div>

          <a href="/" className="text-gray-400 hover:text-[#cf2e2e] transition-colors p-2 -m-2" title="Zur Startseite">
            <Home className="w-5 h-5" />
          </a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {/* Step 1: Instrument Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="relative"
            >
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {publicSettings?.ui_texts?.wizard_step1_title || 'Welches Instrument möchtest du ausprobieren?'}
                </h1>
                <p className="text-gray-500">{publicSettings?.ui_texts?.wizard_step1_subtitle || 'Du kannst auch mehrere auswählen'}</p>
              </div>

              {/* "Ich weiß noch nicht" - prominent call-to-action at top */}
              <div className="mb-8 text-center">
                <motion.button
                  data-testid="instrument-dont-know"
                  onClick={handleDontKnow}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#cf2e2e]/10 to-[#cf2e2e]/5 hover:from-[#cf2e2e]/20 hover:to-[#cf2e2e]/10 text-[#cf2e2e] rounded-2xl text-base font-semibold transition-all border-2 border-[#cf2e2e]/20 hover:border-[#cf2e2e]/40 shadow-sm hover:shadow-md"
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <HelpCircle className="w-5 h-5" />
                  <span>{publicSettings?.ui_texts?.wizard_step1_skip || 'Ich weiß noch nicht – alle Angebote zeigen'}</span>
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </div>

              {/* Search input */}
              <div className="mb-6">
                <input
                  type="text"
                  data-testid="instrument-search"
                  placeholder={publicSettings?.ui_texts?.wizard_step1_search || 'Instrument suchen...'}
                  value={instrumentSearch}
                  onChange={(e) => setInstrumentSearch(e.target.value)}
                  className="w-full bg-white border border-gray-200 rounded-2xl px-5 py-3 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#cf2e2e] transition-colors"
                />
              </div>

              {/* Instruments by group - filtered by search, sorted by group order */}
              <div className="space-y-8 pb-24">
                {sortedGroupNames
                  .map((group) => {
                    const instruments = instrumentsByGroup[group] || [];
                    const filteredInstruments = instrumentSearch
                      ? instruments.filter((i) => 
                          i.name.toLowerCase().includes(instrumentSearch.toLowerCase())
                        )
                      : instruments;
                    return { group, instruments: filteredInstruments };
                  })
                  .filter(({ instruments }) => instruments.length > 0)
                  .map(({ group, instruments }) => (
                  <div key={group}>
                    <h2 className="font-heading text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <span className="text-2xl">{gruppenLookup[group]?.icon || "🎵"}</span>
                      {group}
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {instruments.map((instrument) => {
                        const isSelected = selectedInstruments.includes(instrument.id);
                        return (
                          <motion.button
                            key={instrument.id}
                            data-testid={`instrument-${instrument.id}`}
                            onClick={() => handleInstrumentToggle(instrument.id, instrument.name)}
                            className={`bg-white rounded-2xl p-4 border-2 transition-all text-left group ${
                              isSelected
                                ? "border-[#cf2e2e] shadow-lg bg-[#FFF1F2]"
                                : "border-gray-100 hover:border-[#cf2e2e] hover:shadow-lg"
                            }`}
                            whileHover={{ scale: 1.02, y: -2 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className="flex items-center gap-3">
                              {/* Instrument Icon - only show if enabled in settings */}
                              {publicSettings?.show_instrument_icons && (() => {
                                const IconComp = getInstrumentIcon(instrument.icon);
                                return (
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                    isSelected ? "bg-[#cf2e2e]/10" : "bg-gray-100 group-hover:bg-[#cf2e2e]/10"
                                  }`}>
                                    <IconComp className={`w-4 h-4 ${
                                      isSelected ? "text-[#cf2e2e]" : "text-gray-500 group-hover:text-[#cf2e2e]"
                                    }`} />
                                  </div>
                                );
                              })()}
                              <span 
                                className={`flex-1 font-medium text-sm sm:text-base leading-tight ${isSelected ? "text-[#cf2e2e]" : "text-gray-800 group-hover:text-[#cf2e2e]"}`}
                                lang="de"
                              >
                                {/* Shorten specific long names on mobile */}
                                <span className="sm:hidden">
                                  {instrument.name === 'Musikalische Früherziehung' ? 'Musik. Früherz.' 
                                    : instrument.name === 'Musikkindergarten' ? 'Musikkiga'
                                    : instrument.name}
                                </span>
                                <span className="hidden sm:inline">{instrument.name}</span>
                              </span>
                              {isSelected ? (
                                <div className="w-5 h-5 bg-[#cf2e2e] rounded-full flex items-center justify-center flex-shrink-0">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              ) : (
                                <div className="w-5 h-5 border-2 border-gray-200 rounded-full group-hover:border-[#cf2e2e] transition-colors flex-shrink-0" />
                              )}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Scroll Indicator - animated arrow pointing down */}
              <ScrollIndicator />
            </motion.div>
          )}

          {/* Sticky Footer - Continue Button for Step 1 */}
          {currentStep === 1 && selectedInstruments.length > 0 && (
            <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-40">
              <div className="max-w-2xl mx-auto flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span className="font-semibold text-[#cf2e2e]">
                    {selectedInstruments.length === 1 
                      ? instrumente.find(i => i.id === selectedInstruments[0])?.name
                      : getInstrumentNames(selectedInstruments).join(", ")
                    }
                  </span> gewählt
                </div>
                <motion.button
                  data-testid="continue-to-step2"
                  onClick={handleContinueToStep2}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="rounded-full px-8 py-3 text-base font-bold bg-[#cf2e2e] text-white hover:bg-[#b91c1c] transition-all flex items-center gap-2 shadow-lg"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>Weiter</span>
                  <ChevronRight className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          )}

          {/* Step 2: Gemeinde Selection - Show ALL gemeinden */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {publicSettings?.ui_texts?.wizard_step2_title || 'Wo wohnst du?'}
                </h1>
                <p className="text-gray-500">
                  {publicSettings?.ui_texts?.wizard_step2_subtitle || 'Wähle deine Gemeinde'}
                </p>
              </div>

              {/* Selected instruments summary */}
              {selectedInstruments.length > 0 && (
                <div className="mb-6 flex flex-wrap gap-2 justify-center">
                  {selectedInstruments.map(instId => {
                    const inst = instrumente.find(i => i.id === instId);
                    return inst ? (
                      <span key={instId} className="px-3 py-1 bg-[#FFF1F2] text-[#cf2e2e] rounded-full text-sm font-medium">
                        {inst.name}
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Gemeinden as buttons - show ALL gemeinden without availability hints */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {gemeinden.map((gemeinde) => (
                  <motion.button
                    key={gemeinde.id}
                    data-testid={`gemeinde-btn-${gemeinde.id}`}
                    onClick={() => handleGemeindeSelect(gemeinde.id, gemeinde.name)}
                    className={`bg-white rounded-2xl p-5 border-2 transition-all text-center ${
                      selectedGemeinde === gemeinde.id
                        ? "border-[#cf2e2e] shadow-lg bg-[#FFF1F2]"
                        : "border-gray-100 hover:border-[#cf2e2e] hover:shadow-lg"
                    }`}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <MapPin className={`w-5 h-5 mx-auto mb-2 ${
                      selectedGemeinde === gemeinde.id ? "text-[#cf2e2e]" : "text-gray-400"
                    }`} />
                    <span className={`font-semibold block ${
                      selectedGemeinde === gemeinde.id ? "text-[#cf2e2e]" : "text-gray-800"
                    }`}>
                      {gemeinde.name}
                    </span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Results */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="text-center mb-6">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  {results.length > 0 ? (
                    selectedTeacherName ? (
                      <><span className="text-[#cf2e2e]">{selectedTeacherName}</span> in {selectedGemeindeName}</>
                    ) : selectedInstruments.length === 1 && selectedInstrumentName ? (
                      // Only show instrument name in title if exactly ONE instrument is selected
                      <><span className="text-[#cf2e2e]">{selectedInstrumentName}</span> in {selectedGemeindeName}</>
                    ) : (
                      // For multiple instruments or "show all", just show gemeinde
                      <>Angebote in <span className="text-[#cf2e2e]">{selectedGemeindeName}</span></>
                    )
                  ) : filteredSpecialOffers.length > 0 ? (
                    <>Angebote in <span className="text-[#cf2e2e]">{selectedGemeindeName}</span></>
                  ) : (
                    publicSettings?.ui_texts?.wizard_no_results || "Keine Angebote gefunden"
                  )}
                </h1>
                {results.length > 0 && (
                  <p className="text-gray-500">
                    {filteredResults.length === results.length 
                      ? `${results.length} Termin${results.length !== 1 ? "e" : ""}`
                      : `${filteredResults.length} von ${results.length} Terminen`
                    }
                  </p>
                )}
                
                {/* Teacher filter info banner */}
                {selectedTeacherName && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-[#FFF1F2] rounded-full text-sm"
                  >
                    <span className="text-gray-600">Gefiltert nach Lehrkraft:</span>
                    <span className="font-medium text-[#cf2e2e]">{selectedTeacherName}</span>
                    <button
                      onClick={clearTeacherFilter}
                      className="ml-2 px-2 py-1 bg-white rounded-full text-xs font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                    >
                      Alle anzeigen
                    </button>
                  </motion.div>
                )}
              </div>

              {/* Filter Chips - Desktop: flex-wrap, Mobile: Dropdown */}
              {results.length > 0 && availableInstrumentsInGemeinde.length > 1 && (
                <div className="mb-4">
                  {/* Mobile: Dropdown */}
                  <div className="md:hidden relative">
                    <button
                      onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-left"
                      data-testid="mobile-filter-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Filter:</span>
                        <span className={`font-medium ${activeFilter === "all" ? "text-gray-800" : "text-[#cf2e2e]"}`}>
                          {activeFilter === "all" ? "Alle Instrumente" : activeFilter}
                        </span>
                      </div>
                      <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform ${mobileFilterOpen ? "rotate-180" : ""}`} />
                    </button>
                    
                    {/* Dropdown Menu */}
                    {mobileFilterOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden"
                      >
                        <div className="max-h-64 overflow-y-auto">
                          {/* "Alle" option */}
                          <button
                            onClick={() => {
                              setSelectedInstruments([]);
                              setActiveFilter("all");
                              setMobileFilterOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left flex items-center justify-between border-b ${
                              activeFilter === "all" ? "bg-[#FFF1F2]" : "hover:bg-gray-50"
                            }`}
                          >
                            <span className={activeFilter === "all" ? "text-[#cf2e2e] font-medium" : "text-gray-700"}>
                              Alle Instrumente
                            </span>
                            {activeFilter === "all" && <Check className="h-4 w-4 text-[#cf2e2e]" />}
                          </button>
                          
                          {/* Instrument options */}
                          {availableInstrumentsInGemeinde.map((instrumentName, idx) => (
                            <button
                              key={instrumentName}
                              onClick={() => {
                                handleFilterChange(instrumentName);
                                setMobileFilterOpen(false);
                              }}
                              className={`w-full px-4 py-3 text-left flex items-center justify-between ${
                                idx < availableInstrumentsInGemeinde.length - 1 ? "border-b" : ""
                              } ${activeFilter === instrumentName ? "bg-[#FFF1F2]" : "hover:bg-gray-50"}`}
                            >
                              <span className={activeFilter === instrumentName ? "text-[#cf2e2e] font-medium" : "text-gray-700"}>
                                {instrumentName}
                              </span>
                              {activeFilter === instrumentName && <Check className="h-4 w-4 text-[#cf2e2e]" />}
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </div>
                  
                  {/* Desktop: Flex wrap chips */}
                  <div className="hidden md:flex flex-wrap items-center gap-2">
                    {/* Filter label */}
                    <span className="text-xs text-gray-400 font-medium uppercase tracking-wide mr-1">Filter:</span>
                    
                    {/* "Alle" chip */}
                    <button
                      data-testid="filter-all-location"
                      onClick={() => {
                        setSelectedInstruments([]);
                        setActiveFilter("all");
                      }}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        activeFilter === "all"
                          ? "bg-[#cf2e2e] text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      Alle
                    </button>
                    
                    {/* Instrument chips - no count */}
                    {availableInstrumentsInGemeinde.map((instrumentName) => (
                      <button
                        key={instrumentName}
                        data-testid={`filter-${instrumentName.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => handleFilterChange(instrumentName)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                          activeFilter === instrumentName
                            ? "bg-[#cf2e2e] text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {instrumentName}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Hinweis für nicht verfügbare Instrumente - weißer Kasten */}
              {/* This shows when user selected specific instruments that are not available, but there ARE other results */}
              {selectedInstruments.length > 0 && results.length > 0 && (() => {
                // Find all instrument IDs available in results
                const availableInstrumentIds = new Set();
                results.forEach(r => {
                  if (r.instrument_ids) {
                    r.instrument_ids.forEach(id => availableInstrumentIds.add(id));
                  }
                  if (r.instrument_id) {
                    availableInstrumentIds.add(r.instrument_id);
                  }
                });
                
                // Find instruments that user selected but are not available in this gemeinde
                const notAvailableInstruments = selectedInstruments.filter(instId => {
                  return !availableInstrumentIds.has(instId);
                });
                
                if (notAvailableInstruments.length === 0) return null;
                
                return (
                  <div className="mb-6 bg-white rounded-3xl p-6 shadow-sm border">
                    <div className="space-y-8">
                      {notAvailableInstruments.map(instId => {
                        const inst = instrumente.find(i => i.id === instId);
                        // Find alternative gemeinden that have this instrument
                        const alternativesForInst = gemeinden.filter(g => 
                          g.id !== selectedGemeinde &&
                          angebote.some(a => 
                            a.gemeinde_id === g.id && 
                            (a.instrument_ids?.includes(instId) || a.instrument_id === instId)
                          )
                        );
                        
                        return (
                          <div key={instId} className="text-center pb-8 border-b border-gray-200 last:border-b-0 last:pb-0">
                            <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{
                              __html: getUiText(
                                publicSettings,
                                'wizard_instrument_not_available',
                                { instrument: inst?.name, gemeinde: selectedGemeindeName },
                                'In {gemeinde} wird in dieser Woche leider kein Schnuppertermin für {instrument} angeboten.'
                              )
                                .replace(inst?.name || '', `<span class="font-semibold text-[#cf2e2e]">${inst?.name || ''}</span>`)
                                .replace(selectedGemeindeName, `<span class="font-semibold">${selectedGemeindeName}</span>`)
                            }} />
                            
                            {alternativesForInst.length > 0 ? (
                              <>
                                <p className="text-gray-500 text-sm mb-3">
                                  {getUiText(publicSettings, 'wizard_instrument_available_in', {}, 'Verfügbar in:')}
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center">
                                  {alternativesForInst.map((g) => (
                                    <button
                                      key={g.id}
                                      onClick={() => {
                                        // Set only this instrument
                                        setSelectedInstruments([instId]);
                                        setSelectedInstrument(instId);
                                        setSelectedInstrumentName(inst?.name || "");
                                        handleGemeindeSelect(g.id, g.name, [instId]);
                                      }}
                                      className="px-4 py-2 bg-[#FFF1F2] text-[#cf2e2e] rounded-full text-sm font-medium hover:bg-[#cf2e2e] hover:text-white transition-all"
                                    >
                                      <MapPin className="w-3 h-3 inline-block mr-1" />
                                      {g.name}
                                    </button>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-gray-400 text-sm">
                                {getUiText(publicSettings, 'wizard_instrument_nowhere', {}, 'Leider nirgends verfügbar diese Woche.')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* No results - show alternatives per instrument */}
              {results.length === 0 && filteredSpecialOffers.length === 0 && (
                <div className="bg-white rounded-3xl p-8">
                  {selectedInstruments.length > 0 ? (
                    // Show each instrument separately with its alternatives
                    <div className="space-y-8">
                      {selectedInstruments.map(instId => {
                        const inst = instrumente.find(i => i.id === instId);
                        // Find gemeinden where this specific instrument is available
                        const alternativesForInst = gemeinden.filter(g => 
                          g.id !== selectedGemeinde &&
                          angebote.some(a => 
                            a.gemeinde_id === g.id && 
                            (a.instrument_ids?.includes(instId) || a.instrument_id === instId)
                          )
                        );
                        
                        return (
                          <div key={instId} className="text-center pb-8 border-b border-gray-200 last:border-b-0 last:pb-0">
                            <p className="text-gray-700 mb-4" dangerouslySetInnerHTML={{
                              __html: getUiText(
                                publicSettings,
                                'wizard_instrument_not_available',
                                { instrument: inst?.name, gemeinde: selectedGemeindeName },
                                'In {gemeinde} wird in dieser Woche leider kein Schnuppertermin für {instrument} angeboten.'
                              )
                                .replace(inst?.name || '', `<span class="font-semibold text-[#cf2e2e]">${inst?.name || ''}</span>`)
                                .replace(selectedGemeindeName, `<span class="font-semibold">${selectedGemeindeName}</span>`)
                            }} />
                            
                            {alternativesForInst.length > 0 ? (
                              <>
                                <p className="text-gray-500 text-sm mb-3">
                                  {getUiText(publicSettings, 'wizard_instrument_available_in', {}, 'Verfügbar in:')}
                                </p>
                                <div className="flex flex-wrap gap-2 justify-center">
                                  {alternativesForInst.map((g) => (
                                    <button
                                      key={g.id}
                                      onClick={() => {
                                        // Set only this instrument
                                        setSelectedInstruments([instId]);
                                        setSelectedInstrument(instId);
                                        setSelectedInstrumentName(inst?.name || "");
                                        handleGemeindeSelect(g.id, g.name, [instId]);
                                      }}
                                      className="px-4 py-2 bg-[#FFF1F2] text-[#cf2e2e] rounded-full text-sm font-medium hover:bg-[#cf2e2e] hover:text-white transition-all"
                                    >
                                      <MapPin className="w-3 h-3 inline-block mr-1" />
                                      {g.name}
                                    </button>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-gray-400 text-sm">
                                {getUiText(publicSettings, 'wizard_instrument_nowhere', {}, 'Leider nirgends verfügbar diese Woche.')}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Generic "no results" for "show all" mode
                    <div className="text-center">
                      <p className="text-gray-600 mb-6">
                        {(() => {
                          const text = getUiText(
                            publicSettings,
                            'wizard_no_results_generic',
                            { gemeinde: selectedGemeindeName },
                            `Leider gibt es in ${selectedGemeindeName} gerade keine Angebote.`
                          );
                          
                          const altHint = getUiText(
                            publicSettings,
                            'wizard_alternative_hint',
                            {},
                            'Aber schau gern in einer Nachbargemeinde:'
                          );
                          
                          return (
                            <span dangerouslySetInnerHTML={{
                              __html: text.replace(selectedGemeindeName, `<span class="font-semibold">${selectedGemeindeName}</span>`)
                                + (alternativeGemeinden.length > 0 ? ` ${altHint}` : '')
                            }} />
                          );
                        })()}
                      </p>
                      
                      {alternativeGemeinden.length > 0 && (
                        <div className="flex flex-wrap gap-3 justify-center">
                          {alternativeGemeinden.map((gemeinde) => (
                            <motion.button
                              key={gemeinde.id}
                              data-testid={`alternative-${gemeinde.id}`}
                              onClick={() => handleGemeindeSelect(gemeinde.id, gemeinde.name)}
                              className="px-6 py-3 bg-[#FFF1F2] text-[#cf2e2e] rounded-full font-medium hover:bg-[#cf2e2e] hover:text-white transition-all"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <MapPin className="w-4 h-4 inline-block mr-2" />
                              {gemeinde.name}
                            </motion.button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => setCurrentStep(2)}
                    className="mt-6 w-full text-center text-gray-500 hover:text-[#cf2e2e] underline"
                  >
                    Andere Gemeinde wählen
                  </button>
                </div>
              )}

              {/* Results grouped by instrument, then by day */}
              {results.length > 0 && (
                <div className="space-y-8">
                  {Object.entries(resultsByInstrument)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([instrument, dayGroups]) => (
                      <div key={instrument} className="bg-white rounded-3xl p-6 shadow-sm">
                        {/* Instrument Header - with Gemeinde on right */}
                        <h2 className="font-heading text-2xl font-bold text-[#cf2e2e] mb-6 pb-4 border-b border-gray-100 flex items-center justify-between">
                          <span>{instrument}</span>
                          <span className="text-base font-medium text-gray-400">{selectedGemeindeName}</span>
                        </h2>

                        {/* Days within instrument */}
                        <div className="space-y-6">
                          {weekdayOrder
                            .filter((day) => dayGroups[day])
                            .map((day) => {
                              const dateStr = getDateForWeekday(day);
                              return (
                              <div key={day}>
                                <div className="flex items-center gap-2 mb-4">
                                  <Calendar className="w-5 h-5 text-gray-400" />
                                  <h3 className="font-heading text-lg font-semibold text-gray-800">
                                    {day}
                                    {dateStr && (
                                      <span className="font-normal text-gray-400 ml-2 text-base">
                                        {dateStr}
                                      </span>
                                    )}
                                  </h3>
                                </div>

                                <div className="space-y-3">
                                  {dayGroups[day].map((result) => {
                                    const lehrer = lehrpersonenById[result.lehrperson_id];
                                    return (
                                      <motion.div
                                        key={result.id}
                                        data-testid={`lesson-card-${result.id}`}
                                        className="bg-gray-50 rounded-xl overflow-hidden relative"
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                      >
                                        {/* ========== MOBILE LAYOUT (3 Zeilen) ========== */}
                                        <div className="md:hidden p-4 space-y-2">
                                          {/* Zeile 1: Uhrzeit */}
                                          <div className="flex items-center gap-2">
                                            <Clock className="w-4 h-4 text-[#cf2e2e] flex-shrink-0" />
                                            <span className="font-bold text-gray-900">
                                              {result.zeit_von} – {result.zeit_bis}
                                            </span>
                                          </div>

                                          {/* Zeile 2: Ort */}
                                          <div className="flex items-center gap-2">
                                            <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            <a
                                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                `${result.unterrichtsort_name} ${result.gemeinde_name}`
                                              )}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-gray-700 hover:text-[#cf2e2e] transition-colors flex items-center gap-1.5"
                                              onClick={(e) => e.stopPropagation()}
                                              title="In Google Maps öffnen"
                                            >
                                              {result.unterrichtsort_name}
                                              {/* Room info - only if enabled */}
                                              {publicSettings?.show_room_info && result.raum_anzeigename && (
                                                <span className="text-sm text-gray-400">({result.raum_anzeigename})</span>
                                              )}
                                              <Navigation className="w-3.5 h-3.5 text-gray-400" />
                                            </a>
                                          </div>

                                          {/* Zeile 3: Lehrer - Clickable */}
                                          <button
                                            onClick={() => {
                                              const teacherId = result.lehrperson_id;
                                              const teacherGemeinden = [...new Set(
                                                angebote
                                                  .filter(a => a.lehrperson_id === teacherId)
                                                  .map(a => a.gemeinde_id)
                                              )].map(gId => gemeinden.find(g => g.id === gId)).filter(Boolean);
                                              
                                              setTeacherFilter({
                                                teacher: lehrer || { id: teacherId, name: result.lehrperson_name },
                                                teacherName: result.lehrperson_name,
                                                gemeinden: teacherGemeinden,
                                                currentGemeinde: selectedGemeindeName
                                              });
                                            }}
                                            className="w-full flex items-center gap-2 group text-left pt-2 mt-1 border-t border-gray-200"
                                            data-testid="teacher-trigger"
                                            aria-label={`Details zu ${result.lehrperson_name} anzeigen`}
                                          >
                                            <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden border-2 border-white shadow-sm group-hover:border-[#fecaca] transition-colors flex-shrink-0">
                                              {lehrer?.foto_url ? (
                                                <img
                                                  src={`${API_URL}${lehrer.foto_url}`}
                                                  alt={result.lehrperson_name}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-xs">${result.lehrperson_name?.charAt(0) || '?'}</div>`;
                                                  }}
                                                />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-xs">
                                                  {result.lehrperson_name?.charAt(0) || '?'}
                                                </div>
                                              )}
                                            </div>
                                            <span className="font-medium text-gray-700 group-hover:text-[#cf2e2e] transition-colors">
                                              {result.lehrperson_name}
                                            </span>
                                            <ChevronRight className="w-4 h-4 ml-auto text-gray-300 group-hover:text-[#cf2e2e] transition-colors" />
                                          </button>
                                          
                                          {/* Bemerkung - Mobile */}
                                          {result.bemerkung && result.bemerkung.trim() && (
                                            <p className="text-xs text-gray-500 italic mt-2">
                                              {result.bemerkung}
                                            </p>
                                          )}
                                        </div>

                                        {/* ========== DESKTOP LAYOUT ========== */}
                                        <div className="hidden md:flex items-center p-4 gap-4">
                                          {/* TIME */}
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <Clock className="w-5 h-5 text-[#cf2e2e]" />
                                            <span className="text-lg font-bold text-gray-900 tracking-tight">
                                              {result.zeit_von} – {result.zeit_bis}
                                            </span>
                                          </div>

                                          {/* LOCATION (nur Ort, ohne Gemeinde) */}
                                          <div className="flex items-center gap-2 flex-grow">
                                            <MapPin className="w-4 h-4 text-gray-400" />
                                            <span className="font-medium text-gray-700">{result.unterrichtsort_name}</span>
                                            {/* Room info - only if enabled */}
                                            {publicSettings?.show_room_info && result.raum_anzeigename && (
                                              <span className="text-sm text-gray-400 ml-1">({result.raum_anzeigename})</span>
                                            )}
                                            <a
                                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                `${result.unterrichtsort_name} ${result.gemeinde_name}`
                                              )}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-gray-300 hover:text-[#cf2e2e] transition-colors"
                                              onClick={(e) => e.stopPropagation()}
                                              title="In Google Maps öffnen"
                                            >
                                              <Navigation className="w-3.5 h-3.5 text-gray-400" />
                                            </a>
                                          </div>

                                          {/* TEACHER - Clickable (Foto + Name nebeneinander) */}
                                          <button
                                            onClick={() => {
                                              const teacherId = result.lehrperson_id;
                                              const teacherGemeinden = [...new Set(
                                                angebote
                                                  .filter(a => a.lehrperson_id === teacherId)
                                                  .map(a => a.gemeinde_id)
                                              )].map(gId => gemeinden.find(g => g.id === gId)).filter(Boolean);
                                              
                                              setTeacherFilter({
                                                teacher: lehrer || { id: teacherId, name: result.lehrperson_name },
                                                teacherName: result.lehrperson_name,
                                                gemeinden: teacherGemeinden,
                                                currentGemeinde: selectedGemeindeName
                                              });
                                            }}
                                            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white hover:bg-[#fef2f2] transition-colors group flex-shrink-0 shadow-sm"
                                            data-testid="teacher-trigger-desktop"
                                            aria-label={`Details zu ${result.lehrperson_name} anzeigen`}
                                          >
                                            <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden border-2 border-white shadow-sm group-hover:border-[#fecaca] transition-colors">
                                              {lehrer?.foto_url ? (
                                                <img
                                                  src={`${API_URL}${lehrer.foto_url}`}
                                                  alt={result.lehrperson_name}
                                                  className="w-full h-full object-cover"
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-sm">${result.lehrperson_name?.charAt(0) || '?'}</div>`;
                                                  }}
                                                />
                                              ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-[#FFF1F2] text-[#cf2e2e] font-bold text-sm">
                                                  {result.lehrperson_name?.charAt(0) || '?'}
                                                </div>
                                              )}
                                            </div>
                                            <span className="font-medium text-gray-800 group-hover:text-[#cf2e2e] transition-colors whitespace-nowrap">
                                              {result.lehrperson_name}
                                            </span>
                                            <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#cf2e2e] transition-colors" />
                                          </button>
                                        </div>
                                        
                                        {/* Bemerkung - dezent unter der Karte */}
                                        {result.bemerkung && result.bemerkung.trim() && (
                                          <div className="px-4 pb-3 -mt-1">
                                            <p className="text-xs text-gray-500 italic">
                                              {result.bemerkung}
                                            </p>
                                          </div>
                                        )}
                                      </motion.div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                            })}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Special Offers - dezentes Grau */}
              {filteredSpecialOffers.length > 0 && (
                <div className="space-y-8 mt-8">
                  {filteredSpecialOffers.map((offer) => {
                    const lehrperson = lehrpersonen.find(l => l.id === offer.lehrperson_id);
                    const unterrichtsort = unterrichtsorte.find(u => u.id === offer.unterrichtsort_id);
                    const instrument = instrumente.find(i => i.id === offer.instrument_id);
                    const instrumentName = offer.instrument_name || instrument?.name || "Instrument";
                    
                    // Simple markdown parser for bold and italic
                    const parseMarkdown = (text) => {
                      if (!text) return null;
                      return text
                        .split('\n')
                        .map((line, i) => {
                          // Replace **bold** and *italic*
                          const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
                          return (
                            <span key={i}>
                              {parts.map((part, j) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                  return <strong key={j}>{part.slice(2, -2)}</strong>;
                                } else if (part.startsWith('*') && part.endsWith('*')) {
                                  return <em key={j}>{part.slice(1, -1)}</em>;
                                }
                                return part;
                              })}
                              {i < text.split('\n').length - 1 && <br />}
                            </span>
                          );
                        });
                    };
                    
                    return (
                      <div key={offer.id} className="bg-gray-50 border border-gray-200 rounded-3xl p-6">
                        {/* Instrument Header */}
                        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-200">
                          <h2 className="font-heading text-2xl font-bold text-gray-800">
                            {instrumentName}
                          </h2>
                        </div>
                        
                        {/* Spezialangebot Text */}
                        {offer.spezialangebot_text && (
                          <div className="mb-4 p-3 bg-white rounded-xl text-gray-600 text-sm border border-gray-100">
                            {parseMarkdown(offer.spezialangebot_text)}
                          </div>
                        )}

                        {/* Day header */}
                        <div className="flex items-center gap-2 mb-4">
                          <Calendar className="w-5 h-5 text-gray-400" />
                          <h3 className="font-heading text-lg font-semibold text-gray-700">
                            {offer.wochentag}
                          </h3>
                        </div>

                        {/* Offer details */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="bg-white rounded-2xl border border-gray-100 overflow-hidden"
                        >
                          {/* Mobile Layout */}
                          <div className="md:hidden p-4 space-y-2">
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-500 flex-shrink-0" />
                              <span className="font-bold text-gray-900">
                                {offer.zeit_von} – {offer.zeit_bis}
                              </span>
                            </div>
                            {unterrichtsort && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-700">{unterrichtsort.name}</span>
                              </div>
                            )}
                            {lehrperson && (
                              <button
                                onClick={() => {
                                  const teacherId = offer.lehrperson_id;
                                  const teacherGemeinden = [...new Set(
                                    angebote
                                      .filter(a => a.lehrperson_id === teacherId)
                                      .map(a => a.gemeinde_id)
                                  )].map(gId => gemeinden.find(g => g.id === gId)).filter(Boolean);
                                  
                                  setTeacherFilter({
                                    teacher: lehrperson,
                                    teacherName: `${lehrperson.vorname} ${lehrperson.nachname}`,
                                    gemeinden: teacherGemeinden,
                                    currentGemeinde: selectedGemeindeName
                                  });
                                }}
                                className="w-full flex items-center gap-2 pt-2 mt-1 border-t border-gray-100 group text-left"
                                data-testid="special-teacher-trigger-mobile"
                              >
                                <div className="w-8 h-8 rounded-full bg-gray-100 overflow-hidden border-2 border-white shadow-sm flex-shrink-0 group-hover:border-gray-300 transition-colors">
                                  {lehrperson.foto_url ? (
                                    <img
                                      src={`${API_URL}${lehrperson.foto_url}`}
                                      alt={`${lehrperson.vorname} ${lehrperson.nachname}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-600 font-bold text-xs">
                                      {lehrperson.vorname?.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <span className="font-medium text-gray-700 group-hover:text-gray-600 transition-colors">
                                  {lehrperson.vorname} {lehrperson.nachname}
                                </span>
                                <ChevronRight className="w-4 h-4 ml-auto text-gray-300 group-hover:text-gray-500 transition-colors" />
                              </button>
                            )}
                            
                            {/* Bemerkung - Mobile */}
                            {offer.bemerkung && (
                              <div className="pt-2 mt-2 border-t border-gray-100">
                                <p className="text-xs text-gray-500 italic">
                                  💡 {offer.bemerkung}
                                </p>
                              </div>
                            )}
                          </div>

                          {/* Desktop Layout */}
                          <div className="hidden md:flex items-center p-4 gap-4">
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Clock className="w-5 h-5 text-gray-500" />
                              <span className="text-lg font-bold text-gray-900 tracking-tight">
                                {offer.zeit_von} – {offer.zeit_bis}
                              </span>
                            </div>
                            {unterrichtsort && (
                              <div className="flex items-center gap-2 flex-grow">
                                <MapPin className="w-4 h-4 text-gray-400" />
                                <span className="font-medium text-gray-700">{unterrichtsort.name}</span>
                              </div>
                            )}
                            {lehrperson && (
                              <button
                                onClick={() => {
                                  const teacherId = offer.lehrperson_id;
                                  const teacherGemeinden = [...new Set(
                                    angebote
                                      .filter(a => a.lehrperson_id === teacherId)
                                      .map(a => a.gemeinde_id)
                                  )].map(gId => gemeinden.find(g => g.id === gId)).filter(Boolean);
                                  
                                  setTeacherFilter({
                                    teacher: lehrperson,
                                    teacherName: `${lehrperson.vorname} ${lehrperson.nachname}`,
                                    gemeinden: teacherGemeinden,
                                    currentGemeinde: selectedGemeindeName
                                  });
                                }}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors flex-shrink-0 group cursor-pointer"
                                data-testid="special-teacher-trigger-desktop"
                              >
                                <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden border-2 border-white shadow-sm group-hover:border-gray-300 transition-colors">
                                  {lehrperson.foto_url ? (
                                    <img
                                      src={`${API_URL}${lehrperson.foto_url}`}
                                      alt={`${lehrperson.vorname} ${lehrperson.nachname}`}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-600 font-bold text-sm">
                                      {lehrperson.vorname?.charAt(0)}
                                    </div>
                                  )}
                                </div>
                                <span className="font-medium text-gray-800 whitespace-nowrap group-hover:text-gray-600 transition-colors">
                                  {lehrperson.vorname} {lehrperson.nachname}
                                </span>
                                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                              </button>
                            )}
                          </div>
                          
                          {/* Bemerkung - dezent unten in der Karte */}
                          {offer.bemerkung && (
                            <div className="px-4 pb-3 border-t border-gray-100">
                              <p className="text-xs text-gray-500 italic pt-2">
                                💡 {offer.bemerkung}
                              </p>
                            </div>
                          )}
                        </motion.div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Other Gemeinden - Quick Jump (only show gemeinden with selected instrument) */}
              {results.length > 0 && gemeindenWithSelectedInstrument.length > 1 && (
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <p className="text-center text-gray-500 text-sm mb-4">
                    {selectedInstrumentName 
                      ? `${selectedInstrumentName} in anderen Gemeinden`
                      : "Andere Gemeinden erkunden"
                    }
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {gemeindenWithSelectedInstrument
                      .filter((g) => g.id !== selectedGemeinde)
                      .map((gemeinde) => (
                        <motion.button
                          key={gemeinde.id}
                          data-testid={`jump-to-${gemeinde.id}`}
                          onClick={() => handleGemeindeSelect(gemeinde.id, gemeinde.name)}
                          className="px-4 py-2 bg-white text-gray-600 border border-gray-200 rounded-full text-sm font-medium hover:border-[#cf2e2e] hover:text-[#cf2e2e] transition-all"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          {gemeinde.name}
                        </motion.button>
                      ))}
                  </div>
                  
                  {/* Button to show ALL offers in current Gemeinde */}
                  {selectedInstrumentName && (
                    <div className="mt-6 text-center">
                      <motion.button
                        data-testid="show-all-in-gemeinde-btn"
                        onClick={() => {
                          setSelectedInstruments([]);
                          setActiveFilter("all");
                        }}
                        className="px-6 py-3 bg-[#FFF1F2] text-[#cf2e2e] rounded-full font-medium hover:bg-[#cf2e2e] hover:text-white transition-all"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <MapPin className="w-4 h-4 inline-block mr-2" />
                        {(publicSettings?.ui_texts?.wizard_show_all_offers || 'Alle Angebote in {gemeinde}').replace('{gemeinde}', selectedGemeindeName)}
                      </motion.button>
                    </div>
                  )}
                </div>
              )}

              {/* Start over button */}
              <div className="mt-8 text-center">
                <button
                  data-testid="start-over-btn"
                  onClick={() => navigate("/")}
                  className="px-8 py-3 bg-gray-100 text-gray-700 rounded-full font-medium hover:bg-gray-200 transition-all"
                >
                  Neue Suche starten
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Teacher Filter Overlay - Shows all locations of a teacher */}
      <AnimatePresence>
        {teacherFilter && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setTeacherFilter(null)}
          >
            <motion.div
              className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with Photo */}
              <div className="relative">
                {/* Close button */}
                <button
                  onClick={() => setTeacherFilter(null)}
                  className="absolute top-4 right-4 z-10 w-8 h-8 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg"
                  data-testid="close-teacher-filter"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>

                {/* Teacher Photo */}
                {publicSettings?.show_teacher_photo_publicly !== false && (
                  <div className="w-full h-48 bg-gradient-to-br from-[#FFF1F2] to-[#FFE4E6]">
                    {teacherFilter.teacher?.foto_url ? (
                      <img
                        src={`${API_URL}${teacherFilter.teacher.foto_url}`}
                        alt={teacherFilter.teacherName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-6xl font-bold text-[#cf2e2e]/30">
                          {teacherFilter.teacherName?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-6">
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">
                  {teacherFilter.teacherName}
                </h2>
                
                {/* Instruments taught */}
                {teacherFilter.teacher?.instrumente_names && teacherFilter.teacher.instrumente_names.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {teacherFilter.teacher.instrumente_names.map((inst, idx) => (
                      <span key={idx} className="px-3 py-1 bg-[#FFF1F2] text-[#cf2e2e] rounded-full text-sm font-medium">
                        {inst}
                      </span>
                    ))}
                  </div>
                )}

                {/* Contact info - small and subtle */}
                {(teacherFilter.teacher?.email || teacherFilter.teacher?.telefon) && (
                  <div className="text-sm text-gray-500 space-y-1 mb-4">
                    {teacherFilter.teacher?.email && (
                      <a href={`mailto:${teacherFilter.teacher.email}`} className="flex items-center gap-2 hover:text-[#cf2e2e]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {teacherFilter.teacher.email}
                      </a>
                    )}
                    {teacherFilter.teacher?.telefon && (
                      <a href={`tel:${teacherFilter.teacher.telefon}`} className="flex items-center gap-2 hover:text-[#cf2e2e]">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {teacherFilter.teacher.telefon}
                      </a>
                    )}
                  </div>
                )}

                {/* Locations where this teacher teaches */}
                <div className="border-t border-gray-100 pt-4 mt-4">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    {teacherFilter.teacherName} unterrichtet in:
                  </h3>
                  <div className="space-y-2">
                    {teacherFilter.gemeinden.map(g => {
                      const isCurrentGemeinde = g.name === teacherFilter.currentGemeinde;
                      return (
                        <button
                          key={g.id}
                          onClick={() => {
                            setTeacherFilter(null);
                            // Use new function to keep teacher filter when switching gemeinde
                            handleGemeindeSelectWithTeacher(
                              g.id, 
                              g.name, 
                              teacherFilter.teacher.id, 
                              teacherFilter.teacherName
                            );
                          }}
                          className={`w-full px-4 py-3 rounded-xl text-left font-medium transition-all flex items-center gap-3 ${
                            isCurrentGemeinde
                              ? "bg-[#cf2e2e] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-[#FFF1F2] hover:text-[#cf2e2e]"
                          }`}
                        >
                          <MapPin className="w-5 h-5" />
                          <span>{g.name}</span>
                          {isCurrentGemeinde && (
                            <span className="ml-auto text-sm opacity-80">(aktuell)</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Public Footer with Contact & Anmeldung - only on results page */}
      {currentStep === 3 && <PublicFooter settings={publicSettings} />}
    </div>
  );
}
